import { useEffect, useState } from 'react'
import { ExternalLink, Loader2 } from 'lucide-react'
import { Link } from 'react-router-dom'

import type {
  AcknowledgeInvoiceVoidFailure,
  GetInvoiceById,
  GetStripeLoginLink,
  Invoice,
  LineItem,
  RetryInvoiceVoid,
} from '@/shared/domain'
import { hasUnacknowledgedProviderVoidFailure, paymentCardLabel } from '@/shared/domain'
import { formatCurrency, formatDate } from '@/shared/infra/format'
import { reportError } from '@/shared/infra/observability'
import { Button } from '@/shared/ui/primitives'
import { InvoiceDisputePanel } from './invoice-dispute-panel'
import { InvoiceProviderVoidPanel } from './invoice-provider-void-panel'

export type PaymentDetailPanelProps = {
  invoice: Invoice
  orgId: string
  onGetInvoiceById: GetInvoiceById
  onGetStripeLoginLink: GetStripeLoginLink
  onAcknowledgeVoidFailure?: AcknowledgeInvoiceVoidFailure
  onRetryVoid?: RetryInvoiceVoid
  onVoidFailureAcknowledged?: () => void
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </span>
      {children}
    </div>
  )
}

function LineItemsSection({ items, total }: { items: LineItem[]; total: number }) {
  return (
    <Section title="Line items">
      <div className="flex flex-col gap-1">
        <div className="flex items-baseline justify-between gap-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <span className="flex-1">Description</span>
          <span className="w-12 text-right">Qty</span>
          <span className="w-20 text-right">Unit</span>
          <span className="w-24 text-right">Total</span>
        </div>
        {items.map((item, index) => (
          <div
            key={item.id ?? index}
            className="flex items-baseline justify-between gap-4 py-0.5 text-sm"
          >
            <span className="flex-1">{item.description}</span>
            <span className="w-12 text-right tabular-nums">{item.quantity}</span>
            <span className="w-20 text-right tabular-nums">
              {formatCurrency(item.unitPrice)}
            </span>
            <span className="w-24 text-right tabular-nums">
              {formatCurrency(item.quantity * item.unitPrice)}
            </span>
          </div>
        ))}
        <div className="mt-1 flex items-baseline justify-between gap-4 border-t pt-1 text-sm font-semibold">
          <span className="flex-1">Total</span>
          <span className="w-24 text-right tabular-nums">{formatCurrency(total)}</span>
        </div>
      </div>
    </Section>
  )
}

export function PaymentDetailPanel({
  invoice,
  orgId,
  onGetInvoiceById,
  onGetStripeLoginLink,
  onAcknowledgeVoidFailure,
  onRetryVoid,
  onVoidFailureAcknowledged,
}: PaymentDetailPanelProps) {
  const [hydratedLineItems, setHydratedLineItems] = useState<LineItem[] | null>(
    invoice.lineItems ?? null,
  )
  const [isLoadingLineItems, setIsLoadingLineItems] = useState<boolean>(false)
  const [isOpeningStripe, setIsOpeningStripe] = useState<boolean>(false)
  const [stripeError, setStripeError] = useState<string | null>(null)

  const openStripeDashboard = async () => {
    const result = await onGetStripeLoginLink({ orgId, orgType: 'outfitter' })
    window.open(result.url, '_blank', 'noopener,noreferrer')
  }

  const handleViewInStripe = async () => {
    setStripeError(null)
    setIsOpeningStripe(true)
    try {
      await openStripeDashboard()
    } catch (err) {
      const message = reportError(err, {
        fallbackMessage: 'Failed to open the Stripe dashboard. Please try again.',
      })
      setStripeError(message)
    } finally {
      setIsOpeningStripe(false)
    }
  }

  useEffect(() => {
    if (invoice.lineItems) {
      setHydratedLineItems(invoice.lineItems)
      return
    }
    let cancelled = false
    setIsLoadingLineItems(true)
    onGetInvoiceById({ id: invoice.id, orgId })
      .then((full) => {
        if (cancelled) return
        setHydratedLineItems(full.lineItems ?? [])
      })
      .catch((err) => {
        if (cancelled) return
        reportError(err, { fallbackMessage: 'Failed to load invoice line items.' })
        setHydratedLineItems([])
      })
      .finally(() => {
        if (cancelled) return
        setIsLoadingLineItems(false)
      })
    return () => {
      cancelled = true
    }
  }, [invoice.id, invoice.lineItems, orgId, onGetInvoiceById])

  const showStripeFee =
    invoice.paymentMethod === 'stripe' && invoice.platformFee > 0
  const feeLabel = invoice.checkoutMode === 'bb_checkout' ? 'Processing fee' : 'Platform fee'
  const payUrl = invoice.payUrl ?? invoice.stripePaymentLinkUrl
  const showRefundReason =
    invoice.status === 'refunded' && Boolean(invoice.refundReason)
  const showFailureReason =
    invoice.status === 'failed' && Boolean(invoice.failureReason)
  const hasLinks =
    invoice.bookingId || invoice.providerInvoiceId || payUrl
  const lineItems = hydratedLineItems ?? []
  const lineItemsTotal = lineItems.reduce(
    (sum, item) => sum + item.quantity * item.unitPrice,
    0,
  )

  return (
    <div className="flex flex-col gap-3">
      {onAcknowledgeVoidFailure &&
      onRetryVoid &&
      hasUnacknowledgedProviderVoidFailure(invoice) ? (
        <InvoiceProviderVoidPanel
          invoice={invoice}
          onRetry={async () => {
            await onRetryVoid({ id: invoice.id, orgId })
            onVoidFailureAcknowledged?.()
          }}
          onAcknowledge={async () => {
            await onAcknowledgeVoidFailure({ id: invoice.id, orgId })
            onVoidFailureAcknowledged?.()
          }}
        />
      ) : null}
      {invoice.dispute ? (
        <InvoiceDisputePanel
          dispute={invoice.dispute}
          onRespond={openStripeDashboard}
        />
      ) : null}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Section title="Details">
          <Field label="Client" value={invoice.clientName ?? '—'} />
          <Field label="Email" value={invoice.recipientEmail ?? '—'} />
          <Field label="Created" value={formatDate(invoice.createdAt) || '—'} />
          <Field label="Sent" value={formatDate(invoice.sentAt) || '—'} />
        </Section>
        <Section title="Payment">
          <Field label="Amount" value={formatCurrency(invoice.amount)} />
          <Field label="Paid" value={formatCurrency(invoice.amountPaid)} />
          <Field label="Due" value={formatDate(invoice.dueDate) || '—'} />
          <Field label="Method" value={invoice.paymentMethod ?? '—'} />
          {showStripeFee ? (
            <Field label={feeLabel} value={formatCurrency(invoice.platformFee)} />
          ) : null}
          {invoice.paymentCard ? (
            <Field
              label="Card"
              value={paymentCardLabel(invoice.paymentCard.brand, invoice.paymentCard.last4)}
            />
          ) : null}
        </Section>
      </div>

      {isLoadingLineItems ? (
        <Section title="Line items">
          <div className="flex items-center gap-2 py-1 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Loading line items
          </div>
        </Section>
      ) : lineItems.length > 0 ? (
        <LineItemsSection items={lineItems} total={lineItemsTotal} />
      ) : null}

      {invoice.notes || showRefundReason || showFailureReason ? (
        <Section title="Notes">
          {invoice.notes ? (
            <p className="whitespace-pre-wrap text-sm">{invoice.notes}</p>
          ) : null}
          {showRefundReason ? (
            <div className="mt-1">
              <span className="text-xs text-muted-foreground">Refund reason: </span>
              <span className="text-sm">{invoice.refundReason}</span>
            </div>
          ) : null}
          {showFailureReason ? (
            <div className="mt-1">
              <span className="text-xs text-muted-foreground">Failure reason: </span>
              <span className="text-sm">{invoice.failureReason}</span>
            </div>
          ) : null}
        </Section>
      ) : null}

      {hasLinks ? (
        <Section title="Links">
          <div className="flex items-center gap-4">
            {invoice.bookingId ? (
              <Link
                to={`/bookings/${invoice.bookingId}`}
                className="text-sm text-primary hover:underline"
              >
                View Booking →
              </Link>
            ) : null}
            {invoice.providerInvoiceId ? (
              <Button
                type="button"
                variant="link"
                onClick={handleViewInStripe}
                disabled={isOpeningStripe}
                className="h-auto gap-1 p-0 text-sm"
              >
                View in Stripe
                {isOpeningStripe ? (
                  <Loader2 className="size-3 animate-spin" />
                ) : (
                  <ExternalLink className="size-3" />
                )}
              </Button>
            ) : null}
            {payUrl ? (
              <Button
                type="button"
                variant="link"
                onClick={() => window.open(payUrl, '_blank', 'noopener,noreferrer')}
                className="h-auto gap-1 p-0 text-sm"
              >
                Open pay page
                <ExternalLink className="size-3" />
              </Button>
            ) : null}
          </div>
          {stripeError ? (
            <p className="mt-1 text-xs text-destructive">{stripeError}</p>
          ) : null}
          <span className="mt-1 block font-mono text-xs text-muted-foreground">{invoice.id}</span>
        </Section>
      ) : (
        <div className="rounded-lg border bg-card p-3">
          <span className="font-mono text-xs text-muted-foreground">{invoice.id}</span>
        </div>
      )}
    </div>
  )
}
